// -----------------------------------------------------------------------------
// API Request / Response types
// -----------------------------------------------------------------------------

export interface SsoProvider {
    id: number;
    name: string;
    logoUrl?: string;
    issuer?: string;
    callbackPath?: string;
    isPublicProvider?: boolean;
}

export interface LoginProvidersResponse {
    password: boolean;
    ssoProviders: SsoProvider[];
}

export interface LoginRequest {
    email: string;
    password: string;
    turnstileToken?: string;
    returnUrl?: string;
}

export interface LoginResponse {
    success: boolean;
    returnUrl?: string;
    requireVerification?: boolean;
    errorMessage?: string;
    configure?: boolean;
    passwordBreachWarning?: boolean;
    errorCode?: number;
}

export interface ForgotPasswordRequest {
    email: string;
    turnstileToken?: string;
}

export interface ResetPasswordRequest {
    email: string;
    password: string;
    confirmPassword: string;
    code: string;
    ignoreBreachWarning?: boolean;
}

export interface ValidateResetTokenRequest {
    email: string;
    code: string;
}

export interface ValidateResetTokenResponse {
    success: boolean;
    forceSsoLogin?: boolean;
    allowedSsoProviders?: SsoProvider[];
    errorMessage?: string;
    errorCode?: number;
}

// Two-factor provider is a simple string, e.g. 'Email' | 'Phone'
export type TwoFactorProvider = string;

export interface TwoFactorProvidersResponse {
    success: boolean;
    providers?: TwoFactorProvider[];
    invalidCookie?: boolean;
}

export interface SendCodeRequest {
    provider: string;
}

export interface VerifyCodeRequest {
    code: string;
    provider: string;
    rememberBrowser: boolean;
    returnUrl?: string;
}

export interface VerifyCodeResponse {
    success: boolean;
    returnUrl?: string;
    lockedOut?: boolean;
}

export interface VerifyPhoneRequest {
    code: string;
    phoneNumber: string;
}

export interface UserCountryResponse {
    success: boolean;
    countryIsoCode?: string;
}

export enum PolicyDocumentType {
    TermsAndConditions = 1,
    PrivacyPolicy = 2,
}

export enum PolicyDocumentSource {
    File = 1,
    Link = 2,
}

export interface PolicyDocument {
    type: PolicyDocumentType;
    source: PolicyDocumentSource;
    version: number;
    title: string;
    checkboxLabel: string;
    isUpdate: boolean;
    // An absolute external URL for links, or the relative PrivacyPolicy/Document?type=N for files
    url: string;
}

export interface PolicyDocumentAcceptance {
    type: PolicyDocumentType;
    version: number;
}

export interface PrivacyPolicyInfoResponse {
    success: boolean;
    required?: boolean;
    companyName?: string;
    logoUrl?: string | null;
    // The company's logo for dark backgrounds
    alternateLogoUrl?: string | null;
    email?: string;
    title?: string | null;
    text?: string | null;
    // Only the documents the user still has to accept
    documents?: PolicyDocument[];
}

export interface PrivacyPolicyActionResponse {
    success: boolean;
    error?: string;
    redirectUrl?: string;
}

// -----------------------------------------------------------------------------
// Component prop / state types
// -----------------------------------------------------------------------------

export type LoginStep = 'email' | 'providers' | 'password';

export type ResetPasswordStep =
    | 'loading'
    | 'form'
    | 'success'
    | 'breached'
    | 'expired'
    | 'link-sent';

export type TwoFactorStep = 'provider-select' | 'verify-code' | 'phone-setup';

export type PhoneSetupStep = 'enter-number' | 'verify-code' | 'success';

export interface ServerConfig {
    returnUrl: string;
}
